import type { ImageColumnProps } from '@modules/page-builder/types';
import {
    TileMobileActionsSlot,
    TileOverlayWithFollowSelector,
} from '~/modules/moodboards/components';
import { RevampImageFigure } from './RevampImageFigure';
import { RevampProductMeta } from './RevampProductMeta';
import { RevampProfessionalCtas } from './RevampProfessionalCtas';
import {
    buildModuleProductPanel,
    buildModuleProductTile,
    useModuleProduct,
} from './revamp-module-product';

interface RevampFeaturedProductProps {
    column: ImageColumnProps;
    sizes: string;
    aspectRatio: string;
    /** Width of the picture and its meta; the CTA pair sits outside it. */
    className?: string;
    /** The editor's "Show View Profile / Contact buttons" toggle. */
    showCtaButtons?: boolean;
}

/**
 * The image-and-paragraph module's picture when the editor linked a product.
 *
 * Everything here belongs to the product's supplier, not the practice that
 * wrote the article: Save pins the product (so the board picker marks the
 * board it was saved to), Visit site goes to the supplier's website, and
 * View Profile / Contact go to the supplier's profile.
 */
export const RevampFeaturedProduct = ({
    column,
    sizes,
    aspectRatio,
    className,
    showCtaButtons = false,
}: RevampFeaturedProductProps) => {
    const { product, settled } = useModuleProduct(column);
    const panelActions = buildModuleProductPanel(
        column,
        buildModuleProductTile(column, product),
    );

    return (
        <>
            <div className={className}>
                <div className="relative">
                    <RevampImageFigure
                        column={column}
                        sizes={sizes}
                        className="w-full"
                        aspectRatio={aspectRatio}
                        hideCaption
                        // Featured product is a white-backed cut-out. Paint the
                        // tile, then multiply so that white becomes the fill —
                        // a background on the <img> stays hidden behind the
                        // opaque photo.
                        buttonClassName="overflow-hidden rounded-tile bg-[#f5f5f7]"
                        imgClassName="object-contain object-center mix-blend-multiply"
                        overlay={
                            panelActions ? (
                                <TileOverlayWithFollowSelector
                                    {...panelActions}
                                />
                            ) : undefined
                        }
                    />
                    {panelActions ? (
                        <TileMobileActionsSlot {...panelActions} />
                    ) : null}
                </div>
                <RevampProductMeta
                    column={column}
                    product={product}
                    settled={settled}
                />
            </div>
            {showCtaButtons && product?.professionalLink ? (
                <RevampProfessionalCtas
                    professional={{
                        title: product.professionalName ?? '',
                        link: product.professionalLink,
                    }}
                    placement="imageParagraph"
                />
            ) : null}
        </>
    );
};
